/**
 * Cât timp ține o sesiune fără activitate.
 *
 * The session token itself lives 90 days, so that is not what ends a session.
 * What ends it is going away and coming back much later: until now the app
 * kept the token, failed every request, and rendered page after page with no
 * data on it — signed in, and useless. Twelve hours of no activity now ends
 * the session properly, with a message, instead of leaving that shell behind.
 *
 * Twelve hours is deliberately a working day plus a night: a coach who opens
 * the app in the morning and comes back after evening training is still the
 * same visit, and should not be asked to sign in again in between.
 */

export const IDLE_LIMIT_MS = 12 * 60 * 60 * 1000;

/**
 * How long away counts as "came back" rather than "still here".
 *
 * Under this we leave the page alone; over it we re-check the session and
 * refresh what the app knows, because a tab that sat in the background for
 * half an hour is very likely looking at stale data.
 */
export const AWAY_REFRESH_MS = 5 * 60 * 1000;

const LAST_ACTIVITY_KEY = "igrowth_last_activity";

/**
 * Don't write more often than this.
 *
 * The signal we listen to includes every keystroke, and localStorage writes
 * are synchronous — a round trip to disk in the middle of somebody typing a
 * name. At a twelve-hour limit, knowing when they were last active to within
 * half a minute is plenty.
 */
const WRITE_EVERY_MS = 30_000;
let lastWrite = 0;

/** Shared across tabs on purpose: activity in one is activity in the account. */
export function touchActivity(now: number = Date.now(), force = false): void {
  if (!force && now - lastWrite < WRITE_EVERY_MS) return;
  lastWrite = now;
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
  } catch {
    /* private mode — the in-memory session still works, we just can't share it */
  }
}

/**
 * When the account was last active, or null when we have never recorded it.
 *
 * Null is treated as "now" by the caller rather than as "forever ago": a
 * missing record is our ignorance, not evidence that somebody went away, and
 * signing people out because storage was cleared would be its own bug.
 */
export function lastActivity(): number | null {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

export function clearActivity(): void {
  lastWrite = 0;
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    /* ignore */
  }
}

/** True when the account has been idle past the limit. */
export function isIdleExpired(now: number = Date.now()): boolean {
  const last = lastActivity();
  if (last === null) return false;
  return now - last > IDLE_LIMIT_MS;
}

/** How long the tab was in the background, in ms. 0 when we cannot tell. */
export function awayFor(now: number = Date.now()): number {
  const last = lastActivity();
  return last === null ? 0 : Math.max(0, now - last);
}

/**
 * Why a session ended, so the login screen can say it out loud.
 *
 * "expired" = the server refused the token. "idle" = we ended it ourselves
 * after the limit above. Anything else is an ordinary sign-out and says
 * nothing.
 */
export type SessionEndReason = "expired" | "idle";

export const SESSION_ENDED_EVENT = "igrowth:session-ended";

/**
 * Announce that the session is over.
 *
 * A DOM event rather than a direct call because the axios layer, which is the
 * first to find out (a 401 on any request), must not import the auth context —
 * the context imports axios, and a cycle between them is how one of them ends
 * up undefined at module load.
 */
export function announceSessionEnded(reason: SessionEndReason): void {
  try {
    window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT, { detail: { reason } }));
  } catch {
    /* no DOM (tests, SSR) — nothing to announce to */
  }
}
